import React, { useEffect, useRef, useState } from 'react'
import { Button, Col, Form, Modal, Row } from 'react-bootstrap'
import Select from "react-select";
import UserInfo from '../../../../interfaces/UserInfo';
import { getAllUsersInOrganization } from '../../../../services/userServices/UserInfoService';
import { useAuth } from '../../../../firebase/AuthProvider';
import { toast } from 'react-toastify';
import { CreateCustomGroupRequest, GetChannelRes, UpdateCustomChannelReq } from '../../../../interfaces/team/TeamandRegion.modal';
import { CreateCustomGroupAsync, UpdateCustomGroupAsync } from '../../../../services/teamandregion/TeamandRegionService';
import './createCustomChannelModel.css';
import AvatarImage from '../../../ui/Avatar/AvatarImage';
import { FiSearch } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';

interface CreateCustomChannelModalProps {
    showCreateChannelModal: boolean;
    handleCloseCreateChannelModal: () => void
    refreshChannelList: () => void,
    SelectedChannelForEdit?: GetChannelRes
    onClose?: () => void;
    chatClose?: () => void;

}

const Search = FiSearch as any;

const CreateCustomChannelModal: React.FC<CreateCustomChannelModalProps> = ({
    handleCloseCreateChannelModal,
    showCreateChannelModal,
    refreshChannelList,
    SelectedChannelForEdit,
    onClose,
    chatClose,
}) => {
    const { currentUser } = useAuth();
    const navigate = useNavigate();

    // data
    const [users, setUsers] = useState<UserInfo[]>([]);
    const [filteredUsers, setFilteredUsers] = useState<UserInfo[]>([]);
    const [selectedUsers, setSelectedUsers] = useState<string[]>([]);
    const [channelName, setChannelName] = useState<string>("");

    // other
    const [loading, setLoading] = useState<boolean>(false);
    const [loadingUsers, setLoadingUsers] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>("");

    useEffect(() => {
        if (SelectedChannelForEdit && currentUser) {
            setChannelName(SelectedChannelForEdit.channelName);
            const usersForSet = SelectedChannelForEdit.members
                .filter((u) => u.userId != currentUser.uid)
                .map((u) => u.userId);

            setSelectedUsers(usersForSet);
        }
        GetUsersData();
    }, [currentUser]);

    useEffect(() => {
        if (!searchTerm.trim()) {
            setFilteredUsers(users);
            return;
        }

        const term = searchTerm.toLowerCase();

        const filtered = users.filter((user) => {
            const fullName = `${user.firstName} ${user.lastName}`.toLowerCase();
            const displayName = user.displayName?.toLowerCase() || "";
            const email = user.email?.toLowerCase() || "";

            return (
                fullName.includes(term) ||
                displayName.includes(term) ||
                email.includes(term)
            );
        });

        setFilteredUsers(filtered);
    }, [searchTerm, users]);


    const handleSubmitForm = async () => {
        // validation
        if (!currentUser || !Validator()) return;
        const token = await currentUser.getIdToken();


        let selectedUserForParticipant = selectedUsers;

        // push logged user id also
        selectedUserForParticipant.push(currentUser.uid);

        // if the for is for edit then call update api
        if (SelectedChannelForEdit) {
            // Update the Existing Channel
            const payload: UpdateCustomChannelReq = {
                conversationId: SelectedChannelForEdit.conversationId,
                userIds: selectedUserForParticipant,
                newChannelName: channelName,
                updatedByUserId: currentUser.uid,
            }

            setLoading(true);
            UpdateCustomGroupAsync(token, payload).then((res) => {
                if (res.success) {
                    toast.success(res.message);
                    refreshChannelList();
                    handleCloseCreateChannelModal();

                } else {
                    toast.error(res.message);
                }
            }).catch((err) => {
                toast.error(err.message);
            }).finally(() => {
                setLoading(false);
            })

        } else {
            // Create the new Channel
            const payload: CreateCustomGroupRequest = {
                groupName: channelName,
                createdByUserId: currentUser.uid,
                userIds: selectedUsers
            }

            setLoading(true);
            CreateCustomGroupAsync(token, payload).then((res) => {
                if (res.success) {
                    toast.success(res.message);
                    refreshChannelList();
                    navigate(`/chat/${res?.data?.conversationId}`);
                    handleClose();
                    chatClose?.();
                } else {
                    toast.error(res.message);
                }
            }).catch((err) => {
                toast.error(err.message);
            }).finally(() => {
                setLoading(false);
            })
        }


    };

    const Validator = (): boolean => {
        if (!channelName) {
            toast("Please Enter the Name of channel");
            return false;
        }

        if (selectedUsers.length == 0) {
            toast.warn("Please Select the At least one User");
            return false;
        }

        return true;
    }

    const handleUserSelection = (selectedOptions: any) => {
        setSelectedUsers(
            selectedOptions ? selectedOptions.map((opt: any) => opt.value) : []
        );
    };

    const handleClose = () => {
        handleCloseCreateChannelModal();
        if (onClose) {
            onClose();
        }
    }

    const GetUsersData = async () => {
        try {
            setLoadingUsers(true);
            const token = await currentUser?.getIdToken();
            if (!token) return;
            const allOrgUsers = await getAllUsersInOrganization(token);
            if (allOrgUsers) {
                const filteredOrgUsers = allOrgUsers.filter(
                    u =>
                        u.id !== currentUser?.uid &&
                        u.employeeRole !== "Participant"   // 🚫 exclude Participant
                );
                setUsers(filteredOrgUsers);
            }

        } catch (err) {
            console.error("Failed to load conversations:", err);
        } finally {
            setLoadingUsers(false);
        }
    };

    const renderUsers = searchTerm.trim() ? filteredUsers : users;


    const UserSkeleton = () => (
        <div className="mb-1 flex items-center justify-between rounded-[10px] px-3 py-2 bg-[#F4F4F4] animate-pulse">
            <div className="flex gap-x-3 items-center">
                <div className="w-14 h-14 rounded-full bg-gray-300" />
                <div className="space-y-2">
                    <div className="h-3 w-32 bg-gray-300 rounded" />
                    <div className="h-3 w-40 bg-gray-200 rounded" />
                </div>
            </div>
            <div className="h-8 w-8 rounded-md bg-gray-300" />
        </div>
    );

    const sortedUsers = [...renderUsers].sort((a, b) => {
        const aSelected = selectedUsers.includes(a.id);
        const bSelected = selectedUsers.includes(b.id);

        if (aSelected && !bSelected) return -1;
        if (!aSelected && bSelected) return 1;
        return 0;
    });


    return (
        <div className=''>
            {/* <Modal show={showCreateChannelModal} onHide={handleCloseCreateChannelModal} centered className='custom_popup'>
                <Modal.Header closeButton>
                    <Modal.Title>Create Custom Channel</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form>
                        <Row>
                            <Form.Group className="mb-3">
                                <Form.Label>Group Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={channelName}
                                    onChange={(e) => setChannelName(e.target.value)}
                                    placeholder="Enter group name"
                                />
                            </Form.Group>
                        </Row>
                        <Row>
                            <Form.Group className="custom_input_flled" controlId="">
                                <Form.Label>Select User</Form.Label>
                                <Select
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isMulti
                                    options={users.map((user) => ({
                                        value: user.id,
                                        label:
                                            user.displayName || `${user.firstName} ${user.lastName}`,
                                    }))}
                                    value={selectedUsers.map((uid) => ({
                                        value: uid,
                                        label: users.find((u) => u.id === uid)?.displayName || uid,
                                    }))}
                                    onChange={handleUserSelection}
                                    placeholder="Select users..."
                                />
                            </Form.Group>

                        </Row>
                    </Form>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleCloseCreateChannelModal}>
                        Cancel
                    </Button>
                    <Button
                        variant="primary"
                        onClick={handleSubmitForm}
                        disabled={!channelName.trim() || selectedUsers.length == 0}
                    >
                        {SelectedChannelForEdit ? "Update Group" : "Create Group"}
                    </Button>
                </Modal.Footer>
            </Modal> */}

            <div className="pb-2 border-b-[1.5px] border-[#E9E9E9] mt-[16px]">
                <p className="text-[#6E6E73] m-0 mb-2 text-sm">Group Name</p>
                <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2 flex-1">
                        <input
                            style={{
                                border: "none",
                                background: "transparent",
                                outline: "none",
                                boxShadow: "none",
                                padding: "0px"
                            }}
                            type="text"
                            value={channelName}
                            onChange={(e) => setChannelName(e.target.value)}
                            placeholder="Enter group name"
                            className="flex-1 text-base bg-white text-gray-900 placeholder:text-black"
                            autoFocus
                        />
                    </div>
                </div>
            </div>

            <div className="flex items-center bg-[#F9FAFB] rounded-[10px] px-[12px] mt-[18px]" style={{ border: "1px #E5E7EB solid" }}>
                <Search className=" text-[#99A1AF] " />
                <input
                    placeholder="Search by name or email"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{
                        width: "100%",
                        border: "none",
                        background: "transparent",
                        outline: "none",
                        boxShadow: "none",
                        color: "#0A0A0A80",
                        fontWeight: "400",
                    }}
                />
            </div>

            <div className="max-h-[260px] overflow-y-auto my-3 no-scrollbar">
                {loadingUsers ? (
                    <>
                        {Array.from({ length: 5 }).map((_, i) => (
                            <UserSkeleton key={i} />
                        ))}
                    </>
                ) : users.length === 0 || sortedUsers.length === 0 ? (
                    <p className="text-center text-[#676767] py-10">
                        No users found!
                    </p>
                ) : (
                    sortedUsers.map((user) => {
                        const isSelected = selectedUsers.includes(user.id);
                        const name =
                            user.displayName || `${user.firstName} ${user.lastName}`;

                        return (
                            <div
                                key={user.id}
                                onClick={() => {
                                    let updatedUserIds: string[];

                                    if (isSelected) {
                                        updatedUserIds = selectedUsers.filter(
                                            (id) => id !== user.id
                                        );
                                    } else {
                                        updatedUserIds = [...selectedUsers, user.id];
                                    }

                                    // 🔥 MATCH react-select OUTPUT
                                    handleUserSelection(
                                        updatedUserIds.map((id) => {
                                            const u = users.find((x) => x.id === id);
                                            return {
                                                value: id,
                                                label:
                                                    u?.displayName ||
                                                    `${u?.firstName} ${u?.lastName}`,
                                            };
                                        })
                                    );
                                }}
                                className={`mb-1 flex cursor-pointer items-center justify-between rounded-[10px] px-3 py-2 ${isSelected
                                    ? "bg-[#F4F4F4]"
                                    : "hover:bg-[#F4F4F4]"
                                    }`}
                            >
                                <div className="flex gap-x-3 items-center">
                                    <div
                                        className="relative w-14 h-14 flex-shrink-0 rounded-full flex justify-center items-center"
                                        style={{ border: "2px solid #6999FF" }}
                                    >
                                        <AvatarImage
                                            userId={user.id}
                                            size="sm"
                                            displayName={`${user.firstName} ${user.lastName}`}
                                        />
                                    </div>

                                    <div>
                                        <p className="text-[13px] font-medium text-[#101828] leading-tight m-0 capitalize">
                                            {name}
                                        </p>
                                        <p className="text-[11px] text-[#6A7282] m-0 pt-[2px] uppercase">
                                            {user.employeeRole}
                                        </p>
                                    </div>
                                </div>

                                <div
                                    className={`flex h-8 w-8 items-center justify-center rounded-md border ${isSelected
                                        ? "border-[#7E57C2] bg-[#7E57C2] hover:bg-[#6e43b8]"
                                        : "border-gray-300"
                                        }`}
                                >
                                    {isSelected && (
                                        <svg
                                            className="h-5 w-5 text-white"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                        >
                                            <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                            />
                                        </svg>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t">
                <button
                    onClick={handleClose}
                    className="rounded-[10px] bg-[#F9F6FF] border px-4 py-2 text-sm"
                >
                    Cancel
                </button>

                <button
                    onClick={handleSubmitForm}
                    disabled={loading || !channelName.trim() || selectedUsers.length === 0}
                    className="rounded-[10px] bg-[#7E57C2] hover:bg-[#6e43b8] px-4 py-2 text-sm text-white disabled:opacity-50"
                >
                    {loading
                        ? "Loading..."
                        : SelectedChannelForEdit
                            ? "Update Group"
                            : "Create Group"}
                </button>
            </div>
        </div>
    )
}

export default CreateCustomChannelModal